/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { faBell } from '@fortawesome/free-regular-svg-icons';
import Moment from 'moment';
import Module from './Module';
import LoadingBar from '../../../../Components/LoadingBar/LoadingBar';
import DataPanel from '../../../../Components/DataPanel/DataPanel';
import XpektorButton from '../../../../Components/XpektorButton/XpektorButton';
import { TranslateTriggers } from '../../../../Components/Utilities/TriggerTexts/DefaultTriggers';
import GraphPreviewImage from '../../../../Images/graphPreviewImage.png';

const gT = (t, key) => t(`company-info-modules.equity${key}`);
const gsT = (t, key) => t(`shared${key}`);

const clampToNegative = (value) => (Number(value) > 0 ? 0 : value);

const formatDateTime = (dateTime) => {
  return Moment.utc(dateTime).local().format('YYYY-MM-DD HH:mm');
};

export default function Equity({
  equityStatus,
  onPdfClick,
  onHistoryClick,
  onTriggerClick,
  t,
}) {
  const [translatedTriggers, setTranslatedTriggers] = useState([]);

  useEffect(() => {
    if (equityStatus !== null && Object.keys(equityStatus).length > 0) {
      setTranslatedTriggers(TranslateTriggers(equityStatus.triggers));
    }
  }, [equityStatus]);

  return (
    <Module>
      {!equityStatus ? (
        <p className="module-empty-text">{gT(t, '.module-empty-text')}</p>
      ) : !equityStatus?.financialReport ? (
        <LoadingBar active status={gT(t, '.loading-bar')} relative />
      ) : (
        <>
          <Module.Nav>
            <div />
            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
              <XpektorButton
                icon={faDownload}
                content={gT(t, '.xpektor-btn.save-as-pdf')}
                onClick={() =>
                  onPdfClick(() =>
                    document.getElementById('equity-pdf-wrapper')
                  )
                }
              />
            </div>
          </Module.Nav>
          <div id="equity-pdf-wrapper">
            <Module.KeyFigureSection>
              <Module.KeyFigure
                title={gT(t, '.key-figure.title.share-capital')}
                value={equityStatus.financialReport.shareCapital}
                valueType="currency"
                info={gT(t, '.key-figure.info.share-capital')}
              />
              <Module.KeyFigure
                title={gT(t, '.key-figure.title.result')}
                value={equityStatus.financialReport.result * -1}
                valueType="currency"
                info={gT(t, '.key-figure.info.result')}
              />
              <Module.KeyFigure
                title={gT(t, '.key-figure.title.calculated-result-before-tax')}
                value={
                  equityStatus.financialReport.calculatedResultBeforeTax * -1
                }
                valueType="currency"
                subTitle={gT(
                  t,
                  '.key-figure.sub-title.calculated-result-before-tax'
                )}
                info={gT(t, '.key-figure.info.calculated-result-before-tax')}
              />
              <Module.KeyFigure
                title={gT(t, '.key-figure.title.calculated-result-after-tax')}
                value={
                  equityStatus.financialReport.calculatedResultAfterTax * -1
                }
                valueType="currency"
                subTitle={gT(
                  t,
                  '.key-figure.sub-title.calculated-result-after-tax'
                )}
                info={gT(t, '.key-figure.info.calculated-result-after-tax')}
              />
              <Module.KeyFigure
                title={gT(t, '.key-figure.title.equity-share-capital')}
                value={
                  (equityStatus.financialReport.equity +
                    equityStatus.financialReport.shareCapital / 2 +
                    equityStatus.financialReport.result) *
                  -1
                }
                valueType="currency"
                info={gT(t, '.key-figure.info.equity-share-capital')}
              />
            </Module.KeyFigureSection>
            <Module.DataSection>
              <Module.DataColumn>
                <DataPanel title={gT(t, '.data-panel.title.history')}>
                  <img src={GraphPreviewImage} alt="graph-preview" />
                  <DataPanel.Button
                    text={gT(t, '.data-panel.btn.history')}
                    onClick={onHistoryClick}
                  />
                </DataPanel>
                <DataPanel title={gT(t, '.data-panel.title.reserve')}>
                  <DataPanel.ValueEntry
                    label={gT(t, '.value-entry.label.reserve')}
                    value={equityStatus.financialReport.untaxedReservesSum * -1}
                    valueType="currency"
                    info={gT(t, '.value-entry.info.reserve')}
                  />
                </DataPanel>
                <DataPanel
                  title={gT(t, '.data-panel.title.to-keep-in-mind')}
                  icon={faBell}
                  scroll
                  maxHeight={400}
                >
                  {translatedTriggers.map((data) => (
                    <DataPanel.TriggerEntry
                      text={data.texts.name}
                      onClick={() => onTriggerClick(data.texts)}
                      key={`${data.id} equity`}
                    />
                  ))}
                </DataPanel>
              </Module.DataColumn>
              <Module.DataColumn>
                <DataPanel title={gT(t, '.data-panel.title.invoices')}>
                  <DataPanel.Subtitle
                    title={gT(t, '.subtitle.customer-invoices.0')}
                    info={gT(t, '.subtitle.customer-invoices.1')}
                  />
                  <DataPanel.ValueEntry
                    label={gT(t, '.value-entry.label.non-accounted-invoices')}
                    value={
                      equityStatus.financialReport.invoiceSum ??
                      gsT(t, '.labels.not-available')
                    }
                    valueType={
                      equityStatus.financialReport.invoiceSum !== null
                        ? 'currency'
                        : ''
                    }
                  />
                  <DataPanel.Subtitle
                    title={gT(t, '.subtitle.supplier-invoices.0')}
                    info={gT(t, '.subtitle.supplier-invoices.1')}
                  />
                  <DataPanel.ValueEntry
                    label={gT(t, '.value-entry.label.non-accounted-invoices')}
                    value={
                      equityStatus.financialReport.supplierInvoiceSum !== null
                        ? equityStatus.financialReport.supplierInvoiceSum * -1
                        : gsT(t, '.labels.not-available')
                    }
                    valueType={
                      equityStatus.financialReport.supplierInvoiceSum !== null
                        ? 'currency'
                        : ''
                    }
                  />
                  <DataPanel.Subtitle
                    title={gT(t, '.subtitle.non-accounted-accruals.0')}
                    info={gT(t, '.subtitle.non-accounted-accruals.1')}
                  />
                  <DataPanel.ValueEntry
                    label={gT(t, '.value-entry.label.customer-invoices')}
                    value={
                      equityStatus.financialReport.invoiceAccrualsSum ??
                      gsT(t, '.labels.not-available')
                    }
                    valueType={
                      equityStatus.financialReport.invoiceAccrualsSum !== null
                        ? 'currency'
                        : ''
                    }
                  />
                  <DataPanel.ValueEntry
                    label={gT(t, '.value-entry.label.supplier-invoices')}
                    value={
                      equityStatus.financialReport
                        .supplierInvoiceAccrualsSum !== null
                        ? equityStatus.financialReport
                            .supplierInvoiceAccrualsSum * -1
                        : gsT(t, '.labels.not-available')
                    }
                    valueType={
                      equityStatus.financialReport
                        .supplierInvoiceAccrualsSum !== null
                        ? 'currency'
                        : ''
                    }
                  />
                </DataPanel>
              </Module.DataColumn>
              <Module.DataColumn>
                <DataPanel
                  title={gT(t, '.data-panel.title.calculated-predictive-tax')}
                >
                  <DataPanel.ValueEntry
                    label={gT(t, '.value-entry.label.tax-on-calculated-result')}
                    value={clampToNegative(
                      equityStatus.financialReport.taxOnCalculatedResult
                    )}
                    valueType="currency"
                  />
                  <DataPanel.ValueEntry
                    label={gT(
                      t,
                      '.value-entry.label.tax-on-non-deductable-expenses'
                    )}
                    value={clampToNegative(
                      equityStatus.financialReport.nonDeductibleExpensesSum * -1
                    )}
                    valueType="currency"
                  />
                  <DataPanel.ValueEntry
                    label={gT(t, '.value-entry.label.sum')}
                    value={
                      clampToNegative(
                        equityStatus.financialReport.taxOnCalculatedResult
                      ) +
                      clampToNegative(
                        equityStatus.financialReport.nonDeductibleExpensesSum *
                          -1
                      )
                    }
                    valueType="currency"
                  />
                </DataPanel>
                <DataPanel title={gT(t, '.data-panel.title.other')}>
                  <DataPanel.ValueEntry
                    label={gT(t, '.value-entry.label.latest-voucher')}
                    value={
                      equityStatus.financialReport.latestVoucher
                        ? formatDateTime(
                            equityStatus.financialReport.latestVoucher
                          )
                        : gsT(t, '.labels.not-available')
                    }
                  />
                  <DataPanel.ValueEntry
                    label={gT(t, '.value-entry.label.depreciations')}
                    value={
                      equityStatus.financialReport.depreciationsSum !== null
                        ? equityStatus.financialReport.depreciationsSum * -1
                        : gsT(t, '.labels.not-available')
                    }
                    valueType={
                      equityStatus.financialReport.depreciationsSum !== null
                        ? 'currency'
                        : ''
                    }
                  />
                  <DataPanel.ValueEntry
                    label={gT(
                      t,
                      '.value-entry.label.observation-account-group-1'
                    )}
                    value={equityStatus.financialReport.observationAccount * -1}
                    valueType="currency"
                  />
                  <DataPanel.ValueEntry
                    label={gT(
                      t,
                      '.value-entry.label.observation-account-group-2'
                    )}
                    value={
                      equityStatus.financialReport.observationAccount2 * -1
                    }
                    valueType="currency"
                  />
                </DataPanel>
              </Module.DataColumn>
            </Module.DataSection>
          </div>
        </>
      )}
    </Module>
  );
}
